<script>
import _ from 'lodash'
import planDataMixin from '../../mixins/myPlanData'
import {
  subscribe,
  cancelSubscription,
  renewSubscription
} from '../../services/billingService'
import CreateSubscription from './CreateSubscription'
import CancelSubscription from './CancelSubscription'
import RenewSubscription from './RenewSubscription'

export default {
  name: 'PlansTable',
  components: {
    CreateSubscription,
    CancelSubscription,
    RenewSubscription
  },
  mixins: [planDataMixin],
  props: {
    showFreePlan: {
      default: true,
      type: Boolean,
      required: false
    },
    showFeatures: {
      default: true,
      type: Boolean,
      required: false
    }
  },
  data() {
    const clientId = process.env.VUE_APP_PAYPAL_CLIENT_ID
    return {
      modals: {
        cancel: false
      },
      endpoint: `https://www.paypal.com/sdk/js?client-id=${clientId}&vault=true`,
      paypalSdkLoaded: false
    }
  },
  async mounted() {
    this.$loadScript(this.endpoint).then(() => (this.paypalSdkLoaded = true))
  },
  destroyed() {
    this.$unloadScript(this.endpoint)
  },
  methods: {
    async subscribe(payload) {
      try {
        const billingPlan = await subscribe(payload)
        this.$store.dispatch('setUser', { billingPlan, isSubscribed: true })
        this.$emit('subscribe')

        this.$amplitude.logEvent('Subscription Created', {
          plan_name: this.plan.name,
          subscribed: this.plan.subscribed,
          expires: this.plan.expire
        })

        this.$snackbar.open({
          message: "Subscribed. You're all set to start tracking your health!",
          type: 'is-secondary',
          position: 'is-bottom-left'
        })
      } catch (error) {
        console.log(error)
      }
    },
    async cancelSubscription(note) {
      try {
        const billingPlan = await cancelSubscription({ note })
        this.$store.dispatch('setUser', { billingPlan, isSubscribed: false })

        this.$amplitude.logEvent('Subscription Canceled', {
          plan_name: this.plan.name,
          subscribed: this.plan.subscribed,
          expires: this.plan.expire
        })

        this.modals.cancel = false
        this.$snackbar.open({
          message: 'Your subscription has been canceled.',
          type: 'is-secondary',
          position: 'is-bottom-left'
        })
      } catch (error) {
        console.log(error)
        this.modals.cancel = false
      }
    },
    async revewSubscription(payload) {
      try {
        const billingPlan = await renewSubscription(payload)
        this.$store.dispatch('setUser', { billingPlan, isSubscribed: true })

        this.$snackbar.open({
          message: 'Your subscription has been renewed.',
          type: 'is-secondary',
          position: 'is-bottom-left'
        })
      } catch (error) {
        console.log(error)
      }
    }
  }
}
</script>

<template>
  <div class="plans-table">
    <b-modal :active.sync="modals.cancel" :width="470" scroll="keep">
      <CancelSubscription :expire="expire" @confirm="cancelSubscription" />
    </b-modal>

    <section v-if="!$apollo.loading" class="content is-text is-pricing">
      <div class="pricing-table">
        <div v-if="showFreePlan" class="pricing-plan">
          <div class="plan-header has-text-primary">Neophyte</div>
          <div class="plan-price">
            <span class="plan-price-amount">
              <span class="plan-price-currency">$</span>
              <span class="has-text-primary">0</span>
            </span>
            <br />trial report
            <hr class="spacer" />
          </div>

          <div v-if="showFeatures" class="plan-items">
            <div class="plan-item has-text-primary">
              Visualize basic biomarkers
            </div>
            <div class="plan-item has-text-primary">
              Get health recommendations
            </div>
            <div class="plan-item">—</div>
            <div class="plan-item">—</div>
          </div>
          <div class="plan-footer">
            <button
              v-if="isSubscribed && !plan.canceled"
              class="button is-tertiary is-medium is-fullwidth"
              @click="modals.cancel = true"
            >
              Downgrade to Free
            </button>
            <button
              v-else-if="!!plan.canceled"
              class="button is-tertiary is-medium is-fullwidth"
              disabled="disabled"
              @click="modals.cancel = true"
            >
              Your plan from {{ expire }}
            </button>
            <button
              v-else
              class="button is-medium is-fullwidth"
              disabled="disabled"
            >
              Your current plan
            </button>
          </div>
        </div>
        <div
          :class="{
            'pricing-plan': true,
            'is-active': showFreePlan,
            'is-accent': !plan.canceled && !isSubscribed,
            'is-text': plan.canceled
          }"
        >
          <div class="plan-header has-text-primary">Biohacker</div>
          <div class="plan-price">
            <span class="plan-price-amount">
              <span class="plan-price-currency">$</span>
              <span class="has-text-accent">39</span>
            </span>
            /year
            <hr class="spacer" />
          </div>
          <div v-if="showFeatures" class="plan-items has-text-tertiary">
            <div class="plan-item has-text-primary">
              Visualize advanced biomarkers
            </div>
            <div class="plan-item has-text-primary">
              Get health recommendations
            </div>
            <div class="plan-item has-text-primary">
              Track results over time
            </div>
            <div class="plan-item has-text-primary">
              Print and export reports
            </div>
          </div>
          <div class="plan-footer">
            <div v-if="plan.canceled">
              <RenewSubscription
                :expire="plan.expire"
                @renew="revewSubscription"
              />
              <div>
                <small>
                  Expires <time :datetime="expire">{{ expire }}</time>
                </small>
              </div>
            </div>

            <div v-else-if="!isSubscribed">
              <div v-if="paypalSdkLoaded">
                <CreateSubscription @subscribe="subscribe" />
              </div>
            </div>

            <div v-else>
              <button class="button is-medium is-fullwidth" disabled="disabled">
                Your current plan
              </button>
              <div>
                <small>Renews {{ expire }}</small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<style lang="sass">
.pricing-table
  justify-content: center
.pricing-plan
  max-width: 320px
.is-pricing
  margin: 0 auto
  max-width: 800px
  font-size: 1.4rem
.plan-footer
  line-height: 20px
  small
    font-size: 0.75rem
.subscription-modal
  .modal-card
    width: 100%
  .modal-card-foot
    justify-content: flex-end
  .label
    font-weight: 300
.renew-button
  margin: 8px 0 2px
</style>
